import React, {
	Fragment,
	memo,
	useCallback,
	useRef,
	useState,
	type RefObject,
	useEffect,
} from 'react';
import { styled } from '@compiled/react';
import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { reorder } from '@atlaskit/pragmatic-drag-and-drop/reorder';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ScreenReaderCaption } from '@atlassian/jira-accessibility';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { COLUMN_ID_MEATBALL_MENU } from '../../../common/constants';
import type { Column, ReorderColumn } from '../../../common/types';
import messages from './messages';
import TableHeaderCell from './table-header-cell';

type Props = {
	columns: Column[];
	hasColumnConfiguration: boolean;
	onColumnsChange?: (columns: Column[]) => void;
	tableRef?: RefObject<HTMLTableElement>;
};

function extractIndex(data: Record<string, unknown>) {
	const { index } = data;
	if (typeof index !== 'number') {
		return null;
	}
	return index;
}

type CleanupFn = () => void;

/** Set the `style` property on a `HTMLElement`
 *
 * @returns a `cleanup` function to restore the `style` property to its original state
 */
function setStyle(el: HTMLElement, property: string, value: string): CleanupFn {
	const originalValue = el.style.getPropertyValue(property);
	const originalPriority = el.style.getPropertyPriority(property);
	el.style.setProperty(property, value, 'important');
	return function cleanup() {
		el.style.setProperty(property, originalValue, originalPriority);
	};
}

function isHtmlElement(value: unknown): value is HTMLElement {
	return value instanceof HTMLElement;
}

/**
 * Set the style of a table column to indicate its being dragged.
 * Since Compiled doesn't support dynamic selectors, this approach is used to avoid re-rendering the table cells.
 *
 * @returns an array of `cleanup` functions to restore the `style` property of each cell to its original state
 */
export function setColumnDraggingStyle(table: HTMLElement, columnIndex: number): CleanupFn[] {
	return Array.from(table.querySelectorAll(`td:nth-child(${columnIndex + 1})`))
		.filter(isHtmlElement)
		.map((element) => setStyle(element, 'opacity', '0.4'));
}

/**
 * Runs a flash animation on the background color of the column that has been reordered,
 * in order to highlight where the column has moved to.
 */
function triggerColumnFlash(table: HTMLElement, columnIndex: number): void {
	const columnCellsSelector = `th:nth-child(${columnIndex + 1}), td:nth-child(${columnIndex + 1})`;

	Array.from(table.querySelectorAll(columnCellsSelector))
		.filter(isHtmlElement)
		.forEach((element) => triggerPostMoveFlash(element));
}

type ReorderOperation = {
	finishIndex: number;
};

export const TableHeader = memo<Props>(
	({ columns, hasColumnConfiguration, onColumnsChange, tableRef }: Props) => {
		const { formatMessage } = useIntl();
		const { createAnalyticsEvent } = useAnalyticsEvents();
		const tableHeadRef = useRef<HTMLTableSectionElement | null>(null);
		// The clean up functions to restore the `style` property of each cell to its original state
		// are stored in a ref rather than in the useEffect closure as a new drag can start
		// before the column reorder is complete, causing the cleanup functions to be lost.
		const styleCleanupFnsRef = useRef<CleanupFn[]>([]);

		const [instanceId] = useState(() => Symbol('instance-id'));
		const [screenReaderMessage, setScreenReaderMessage] = useState('');
		const [lastReorderOperation, setLastReorderOperation] = useState<ReorderOperation | null>(null);
		// Height of the visible part of the table (i.e. excluding content outside the scroll area)
		// This is used to calculate the height of the column preview image we display while dragging
		const [visibleTableHeight, setVisibleTableHeight] = useState(0);

		const totalColumns = columns.length;
		const isDraggable = hasColumnConfiguration && totalColumns > 2;
		const totalColumnsExcludingMenu = columns.filter(
			({ id }) => Boolean(id) && id !== COLUMN_ID_MEATBALL_MENU,
		).length;

		const reorderColumn: ReorderColumn = useCallback(
			({ startIndex, indexOfTarget, closestEdgeOfTarget = null }) => {
				const finishIndex = getReorderDestinationIndex({
					axis: 'horizontal',
					startIndex,
					indexOfTarget,
					closestEdgeOfTarget,
				});

				if (startIndex === finishIndex) {
					return;
				}

				const newColumns = reorder({
					list: columns,
					startIndex,
					finishIndex,
				});

				onColumnsChange?.(newColumns);

				setLastReorderOperation({ finishIndex });

				setScreenReaderMessage(
					formatMessage(messages.dndMoved, {
						column: columns[startIndex]?.title,
						previousPosition: startIndex + 1,
						position: finishIndex + 1,
						total: totalColumnsExcludingMenu,
					}),
				);

				fireUIAnalytics(
					createAnalyticsEvent({
						action: 'reordered',
						actionSubject: 'columns',
					}),
					'issueTable',
				);
			},
			[columns, createAnalyticsEvent, formatMessage, onColumnsChange, totalColumnsExcludingMenu],
		);

		useEffect(
			() =>
				monitorForElements({
					canMonitor({ source }) {
						return source.data.instanceId === instanceId;
					},
					onGenerateDragPreview() {
						const tableRect = tableRef?.current?.getBoundingClientRect();
						const tableHeadRect = tableHeadRef.current?.getBoundingClientRect();
						if (tableRect && tableHeadRect) {
							// We calculate the height this way rather than using `tableRect.height` because the table head is sticky
							// and when the table is scrolled, this prevents the drag preview from overflowing the table.
							setVisibleTableHeight(tableRect.bottom - tableHeadRect.top);
						} else {
							setVisibleTableHeight(0);
						}
					},
					onDragStart({ source }) {
						const columnIndex = extractIndex(source.data);
						const table = tableRef?.current;
						if (columnIndex === null || !table) {
							return;
						}

						styleCleanupFnsRef.current = setColumnDraggingStyle(table, columnIndex);
					},
					onDrop({ location, source }) {
						styleCleanupFnsRef.current.forEach((fn) => fn());
						styleCleanupFnsRef.current.length = 0;

						const destination = location.current.dropTargets[0];
						if (!destination) {
							return;
						}

						const indexOfTarget = extractIndex(destination.data);
						const startIndex = extractIndex(source.data);
						if (indexOfTarget === null || startIndex === null) {
							return;
						}

						const closestEdgeOfTarget = extractClosestEdge(destination.data);

						reorderColumn({
							startIndex,
							indexOfTarget,
							closestEdgeOfTarget,
						});
					},
				}),
			[columns, instanceId, reorderColumn, tableRef],
		);

		// We use an object for lastReorderOperation so we can trigger the column flash
		// if the finish index is the same as the previous reorder operation.
		useEffect(() => {
			const table = tableRef?.current;
			if (!table || lastReorderOperation === null) {
				return;
			}

			triggerColumnFlash(table, lastReorderOperation.finishIndex);
		}, [lastReorderOperation, tableRef]);

		return (
			<>
				<ScreenReaderCaption
					aria-live="assertive"
					// eslint-disable-next-line jira/integration/test-id-by-folder-structure
					data-testid="native-issue-table.ui.issue-table.table-header.screen-reader-caption"
				>
					{screenReaderMessage}
				</ScreenReaderCaption>
				<TableHead ref={tableHeadRef}>
					<tr>
						{columns.map((column, index) => (
							<Fragment key={column.id}>
								{column.customHeader !== undefined ? (
									column.customHeader
								) : (
									<TableHeaderCell
										column={column}
										index={index}
										instanceId={instanceId}
										isDraggable={isDraggable}
										reorderColumn={reorderColumn}
										totalColumns={totalColumns}
										totalColumnsExcludingMenu={totalColumnsExcludingMenu}
										visibleTableHeight={visibleTableHeight}
									/>
								)}
							</Fragment>
						))}
					</tr>
				</TableHead>
			</>
		);
	},
);

export default TableHeader;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TableHead = styled.thead({
	position: 'sticky',
	top: 0,
	zIndex: 1,
	backgroundColor: token('elevation.surface', colors.N0),
	border: 'none',
});
